import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClientFactory } from '@doccareplus/api-client';
import { selectActiveContext, selectTokens, useAppSelector } from '@doccareplus/kernel/application';
import { connectNotificationEvents } from '../infrastructure/notificationEvents';
import { notificationKeys } from './notifications.query';
import { useAccountSettingsQuery } from '@features/settings';
import { playNotificationSound } from '../infrastructure/notificationSound';

export function useNotificationLiveUpdates() {
  const client = useQueryClient();
  const tokens = useAppSelector(selectTokens);
  const context = useAppSelector(selectActiveContext);
  const accountSettings = useAccountSettingsQuery();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!tokens?.accessToken || !context?.clinicId || !context.role) return;
    const controller = new AbortController();
    let revision: number | undefined;
    let retry = 0;
    let invalidateTimer: ReturnType<typeof setTimeout> | undefined;
    const invalidateSoon = () => {
      clearTimeout(invalidateTimer);
      invalidateTimer = setTimeout(() => void client.invalidateQueries({ queryKey: notificationKeys.all }), 150);
    };
    const run = async () => {
      while (!controller.signal.aborted) {
        try {
          // A reconnect starts only after the preceding stream closes.
          // oxlint-disable-next-line eslint/no-await-in-loop
          await connectNotificationEvents({
            baseUrl: apiClientFactory.getActiveBaseURL(),
            accessToken: tokens.accessToken,
            clinicId: context.clinicId,
            activeRole: context.role,
            signal: controller.signal,
            lastEventId: revision,
            onOpen: () => { retry = 0; setConnected(true); },
            onEvent: (event) => {
              if (event.revision <= (revision ?? -1) && event.eventType !== 'notification.snapshot.required') return;
              revision = Math.max(revision ?? 0, event.revision);
              if (event.eventType !== 'notification.snapshot.required') playNotificationSound(accountSettings.data?.notificationSoundsEnabled ?? false);
              invalidateSoon();
            },
          });
        } catch {
          if (controller.signal.aborted) return;
        }
        setConnected(false);
        const delays = [1000, 2000, 5000, 10000, 30000];
        // Backoff is intentionally sequential between reconnect attempts.
        // oxlint-disable-next-line eslint/no-await-in-loop
        await delay(delays[Math.min(retry++, delays.length - 1)], controller.signal);
      }
    };
    void run();
    return () => {
      controller.abort();
      clearTimeout(invalidateTimer);
      setConnected(false);
    };
  }, [accountSettings.data?.notificationSoundsEnabled, client, context?.clinicId, context?.role, tokens?.accessToken]);

  return { connected };
}

function delay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, milliseconds);
    signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
  });
}
